import React, { useCallback, useEffect, useState } from 'react';
import {
  TaskyButton,
  TaskyEmptyState,
  TaskyErrorState,
  TaskyInput,
  TaskySpinner,
  TaskyTable,
  TaskyTableBody,
  TaskyTableCell,
  TaskyTableHead,
  TaskyTableHeaderCell,
  TaskyTableRow,
} from '../../tasky';
import adminBehaviorService, {
  BehaviorRange,
  SessionDetail,
  SessionSearchParams,
  SessionSummary,
} from '../../../services/api/adminBehaviorService';
import { DEVICE_LABELS, formatDateTime, formatMs, formatNumber } from './format';
import SessionTimeline from './SessionTimeline';

interface Props {
  range: BehaviorRange;
  initialUserId?: number;
  initialOrganizationId?: number;
}

/**
 * セッション探索。メール / ユーザーID / 組織ID で絞り込み、行を選ぶとタイムラインを表示する。
 */
const SessionsTab: React.FC<Props> = ({ range, initialUserId, initialOrganizationId }) => {
  const [email, setEmail] = useState('');
  const [userId, setUserId] = useState<string>(initialUserId ? String(initialUserId) : '');
  const [organizationId, setOrganizationId] = useState<string>(initialOrganizationId ? String(initialOrganizationId) : '');
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [detail, setDetail] = useState<SessionDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const search = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: SessionSearchParams = { ...range, limit: 50 };
      if (email.trim()) params.email = email.trim();
      if (userId.trim()) params.user_id = Number(userId);
      if (organizationId.trim()) params.organization_id = Number(organizationId);
      setSessions(await adminBehaviorService.getSessions(params));
    } catch {
      setError('セッションの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [range, email, userId, organizationId]);

  // 期間変更と初期表示で検索する（入力中の再検索はボタンで）
  useEffect(() => {
    void search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);

  const openSession = async (sessionId: string): Promise<void> => {
    setSelected(sessionId);
    setDetail(null);
    setDetailLoading(true);
    try {
      setDetail(await adminBehaviorService.getSession(sessionId));
    } catch {
      setError('セッション詳細の取得に失敗しました');
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <form
        className="flex flex-col gap-2 sm:flex-row sm:items-end"
        onSubmit={(e) => {
          e.preventDefault();
          void search();
        }}
      >
        <div className="flex-1">
          <label htmlFor="behavior-session-email" className="mb-1 block text-xs text-warm-text-muted">
            メールアドレス
          </label>
          <TaskyInput id="behavior-session-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@example.com" />
        </div>
        <div className="w-full sm:w-32">
          <label htmlFor="behavior-session-user" className="mb-1 block text-xs text-warm-text-muted">
            ユーザーID
          </label>
          <TaskyInput id="behavior-session-user" value={userId} onChange={(e) => setUserId(e.target.value)} inputMode="numeric" />
        </div>
        <div className="w-full sm:w-32">
          <label htmlFor="behavior-session-org" className="mb-1 block text-xs text-warm-text-muted">
            組織ID
          </label>
          <TaskyInput id="behavior-session-org" value={organizationId} onChange={(e) => setOrganizationId(e.target.value)} inputMode="numeric" />
        </div>
        <TaskyButton type="submit" variant="default" size="sm">
          検索
        </TaskyButton>
      </form>

      {error && <TaskyErrorState title={error} />}

      {loading ? (
        <div className="flex justify-center py-12">
          <TaskySpinner />
        </div>
      ) : sessions.length === 0 ? (
        <TaskyEmptyState title="該当するセッションがありません" description="期間や条件を変えて検索してください" />
      ) : (
        <div className="overflow-x-auto">
          <TaskyTable className="min-w-[960px]">
            <TaskyTableHead>
              <TaskyTableRow>
                <TaskyTableHeaderCell>開始</TaskyTableHeaderCell>
                <TaskyTableHeaderCell>ユーザー</TaskyTableHeaderCell>
                <TaskyTableHeaderCell>組織</TaskyTableHeaderCell>
                <TaskyTableHeaderCell>滞在</TaskyTableHeaderCell>
                <TaskyTableHeaderCell>PV</TaskyTableHeaderCell>
                <TaskyTableHeaderCell>クリック</TaskyTableHeaderCell>
                <TaskyTableHeaderCell>エラー</TaskyTableHeaderCell>
                <TaskyTableHeaderCell>最初 → 最後</TaskyTableHeaderCell>
                <TaskyTableHeaderCell>端末</TaskyTableHeaderCell>
              </TaskyTableRow>
            </TaskyTableHead>
            <TaskyTableBody>
              {sessions.map((session) => (
                <TaskyTableRow
                  key={session.session_id}
                  className={`cursor-pointer ${selected === session.session_id ? 'bg-primary/5' : ''}`}
                  onClick={() => void openSession(session.session_id)}
                  data-testid={`session-row-${session.session_id}`}
                >
                  <TaskyTableCell className="whitespace-nowrap">{formatDateTime(session.started_at)}</TaskyTableCell>
                  <TaskyTableCell>
                    {session.user_name ?? <span className="text-warm-text-muted">匿名</span>}
                    {session.user_email && <div className="text-xs text-warm-text-muted">{session.user_email}</div>}
                  </TaskyTableCell>
                  <TaskyTableCell>{session.organization_name ?? '-'}</TaskyTableCell>
                  <TaskyTableCell>{formatMs(session.duration_ms)}</TaskyTableCell>
                  <TaskyTableCell>{formatNumber(session.page_views)}</TaskyTableCell>
                  <TaskyTableCell>{formatNumber(session.clicks)}</TaskyTableCell>
                  <TaskyTableCell className={session.errors > 0 ? 'text-destructive' : ''}>{formatNumber(session.errors)}</TaskyTableCell>
                  <TaskyTableCell className="font-mono text-xs">
                    {session.first_page ?? '-'} → {session.last_page ?? '-'}
                  </TaskyTableCell>
                  <TaskyTableCell>{DEVICE_LABELS[session.device_category ?? ''] ?? session.device_category ?? '-'}</TaskyTableCell>
                </TaskyTableRow>
              ))}
            </TaskyTableBody>
          </TaskyTable>
        </div>
      )}

      {selected && (
        <div className="rounded-tasky-md border border-warm-border p-4">
          <h3 className="mb-3 text-sm font-semibold text-dark">セッションタイムライン</h3>
          {detailLoading && (
            <div className="flex justify-center py-6">
              <TaskySpinner />
            </div>
          )}
          {!detailLoading && detail && <SessionTimeline detail={detail} />}
        </div>
      )}
    </div>
  );
};

export default SessionsTab;
